import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  IInputModelConfig,
  InputFieldConfigValues,
  invalidInputCharacters,
  SharedConstants,
  SharedValidationPatterns,
  TextType
} from '../..';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-input-field[isRequired][enterPress][inputLabel]',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss']
})
export class InputFieldComponent implements OnInit {
  // This field is a placeholder value of input , it should be pass while calling this component.
  @Input() placeholder: string = SharedConstants.EMPTY_STRING;
  
  //helper text is displayed at the bottom of the input field
  @Input() helperText: string = SharedConstants.EMPTY_STRING;

  // Label for the input field
  @Input() inputLabel: string = SharedConstants.EMPTY_STRING;

  // tooltip icon for the input field
  @Input() tooltipIcon = true;

  // Tooltip delay value, defaults to 0
  @Input() tooltipDelay: string = InputFieldConfigValues.TOOLTIP_DELAY;

  // This field tells whether the input component is reuired or not , should be pass while calling the component.
  @Input() isRequired!: boolean;

  // This is error message when required error is shown.
  @Input() requiredErrorMessage = 'shared.inputField.requiredMessage';

  // This field tells whether the input component is reuired or not , should be pass while calling the component.
  @Input() isDisabled!: boolean;

  // This field specifies the placeholder size.
  @Input() placeholderSize = SharedConstants.EMPTY_STRING;

  // This field specifies the prefix icon of Input field.
  @Input() prefixIcon: string = SharedConstants.EMPTY_STRING;

  // Max length is used for maximum length
  @Input() maxLength: number = InputFieldConfigValues.MAX_LENGTH;

  // MinValue is used to verify min valid value of input
  @Input() minValue: number = InputFieldConfigValues.MIN_VALUE;

  // MaxValue is used to verify max valid value of input
  @Input() maxValue: number = InputFieldConfigValues.MAX_VALUE;

  // used to set input height
  @Input() inputHeight!: string;

  // type is used provide type like text, email etc.
  @Input() inputType!: string;

  // tooltipText is used to display tooltip
  @Input() tooltipText: string;

  // to enable and disble the tooltip
  @Input() showToolTipText = true;

  // id for the input component
  @Input() id!: string;

  // flag for change the title on error/warning
  @Input() dyeTitle = true;

  // flag for showing custom error
  @Input() hasCustomError = false;

  // custom error msg should be a translated string
  @Input() customErrorMsg!: string;

  // custom error msg text type
  @Input() customErrorMsgTextType: TextType = 'body1';

  // flag for showing custom warning
  @Input() hasCustomWarning = false;

  // custom warning msg should be a translated string
  @Input() customWarningMsg!: string;

  // Boolean to specify if the required icon is to be displayed.
  @Input() requiredIconDisplayed = false;

  // Check whether to allow invalid input characters(-,+,e)
  @Input() shouldCheckForInvalidInputCharacters = false;

  // config for custom input validator and event
  @Input() inputModelConfig!: IInputModelConfig;

  // custom class for input field
  @Input() customClassInput: string | object = SharedConstants.EMPTY_STRING;

  // custom class for input field wrapper
  @Input() customClassInputWrapper: string = SharedConstants.EMPTY_STRING;

  // This function will emit the event.target.value on pressing enter key.
  @Output() enterPress: EventEmitter<string> = new EventEmitter<string>();

  // This function will emit the event.target.value on key press.
  @Output() keyPress: EventEmitter<string> = new EventEmitter<string>();

  // This function will emit the event.target.value on key down.
  @Output() keyDown: EventEmitter<string> = new EventEmitter<string>();

  // This function will emit the event.target.value on key down.
  @Output() inputPasteEmiter: EventEmitter<any> = new EventEmitter<any>();

  // This function will emit the event.target.value on something entered or removed.
  @Output() changeInput: EventEmitter<string> = new EventEmitter<string>();

  //This function will emit the closeIconClick event.
  @Output() closeIconClick: EventEmitter<any> = new EventEmitter<any>();

  // This function will change the input value
  @Output() changeInputValue: EventEmitter<string> = new EventEmitter<string>();

  @Output() prefixIconClick: EventEmitter<void> = new EventEmitter<void>();

  // FormControl value for input field.
  @Input() set control(value: FormControl) {
    if (value !== this.inputText) {
      this.inputText = value;
    }
  }
  // default initialization for formcontrol
  inputText: FormControl = new FormControl(SharedConstants.EMPTY_STRING);

  //It is used to check input is focused or not
  focused: boolean;

  // This is used to bind/preserve to the value entered into the license plate number
  @Input() value!: string;

  // To specify the height of the input field
  @Input() height: string = InputFieldConfigValues.INPUT_HEIGHT;

  // To specify custom styles for the input field
  customStyle!: object;

  constructor(private readonly translateService: TranslateService) {
    this.isDisabled = false;
    this.focused = false;
    this.tooltipText = SharedConstants.EMPTY_STRING;
  }

  ngOnInit() {
    this.setCustomStyles();
  }

  /**
   * This function is called to set the custom style
   * for the component.
   */
  setCustomStyles = () => {
    this.customStyle = { height: this.height };
  };

  /**
   * This function will be called when input field will be focused
   */
  onFocus() {
    this.focused = true;
  }

  /**
   * This function will be called when input field will be blured
   */
  onBlur() {
    this.focused = false;
  }

  /**
   * This function will call on clicking cancel icon in input field.
   * It will clear the input text.
   */
  onCancel = () => {
    this.inputText.setValue(SharedConstants.EMPTY_STRING);
    this.inputText.markAsDirty();
    this.closeIconClick.emit();
  };

  /**
   *  This function will return the Input class on the basis on some condition.
   *
   * @return string
   */
  getInputClass = () => {
    let inputClass = 'input-field-untouched';
    if (this.isDisabled) {
      this.inputText.disable();
      inputClass = 'input-field-disabled';
    } else {
      this.inputText.enable();
      if (
        (this.inputText.hasError('required') ||
          this.inputText.hasError('maxlength') ||
          this.inputText.hasError('min') ||
          this.inputText.invalid ||
          this.hasCustomError) &&
        (this.inputText.dirty || this.inputText.touched) // it has all known check as well as check for unknown validators
      ) {
        return 'input-field-touched';
      }

      if ((this.inputText.dirty || this.inputText.touched) && this.hasCustomWarning) {
        return 'input-field-warning';
      }

      if (this.focused && (!this.inputText.invalid || !this.inputText.dirty)) {
        // whenever input field will be focused active class will be added
        inputClass = 'input-field-active';
      }
    }
    return inputClass;
  };

  /**
   * This function will be used to get display status of error symbol
   *
   * @returns boolean
   */
  getDisplayStatus() {
    return (
      (this.isRequired &&
        this.inputText.value === SharedConstants.EMPTY_STRING &&
        this.inputText.touched) ||
      (this.inputText.invalid && this.inputText.dirty) ||
      this.inputText.hasError('maxlength') ||
      this.hasCustomError
    );
  }

  /**
   * This function will trigger on pressing the enter key.
   * It will bind the event.target.value to the inputText
   *
   * @param event
   */
  onKeyEnter(event: any) {
    this.enterPress.emit(event.target.value);
  }

  /**
   * This function will trigger on Key Press.
   *
   * @param event
   */
  onKeyPress(event: any) {
    if (this.inputModelConfig?.hasCustomError) {
      event = event || window.event;
      // get keycode when user input on cross browser
      const key = event.keyCode || event.which;
      if (this.inputModelConfig.event?.['onKeyPress'](event, key)) {
        event.preventDefault();
        return;
      }
    }
    this.keyPress.emit(event.target.value);
  }

  /**
   * This function will trigger on key down.
   *
   * @param {any} event
   */
  onKeyDown = (event: any) => {
    this.shouldCheckForInvalidInputCharacters &&
      invalidInputCharacters.includes(event.key) &&
      event.preventDefault();
    this.keyDown.emit(event.target.value);
  };

  /**
   * This function will trigger when something is changed in input
   *
   * @param event
   */
  onChangeInput(event: any) {
    this.changeInput.emit(event.target.value);
  }

  /**
   * This function will trigger when something is changed in input
   *
   * @param event
   */
  onPaste(event: any) {
    this.inputPasteEmiter.emit(event);
  }

  /**
   * This function will trigger when input value is
   * changed. Also checks if the string/number should
   * not contain +,- and e.
   *
   * @param {any} event
   */
  onChangeInputValueEmitter = (event: any) => {
    let changedValue: string = event.target.value;
    if (this.shouldCheckForInvalidInputCharacters) {
      changedValue = changedValue.replace(
        SharedValidationPatterns.INVALID_CHARACTER_INPUT_REGEX,
        SharedConstants.EMPTY_STRING
      );
      this.inputText.setValue(changedValue);
    }

    this.changeInputValue.emit(changedValue);
  };

  /**
   * Returns the tooltip text if provided or else
   * default tooltip text is shown.
   *
   * @returns {string} tooltip text
   */
  getTooltipText = () =>
    this.tooltipText === SharedConstants.EMPTY_STRING
      ? this.translateService.instant('system.form.tooltipText')
      : this.tooltipText;

  /**
   * Checks whether to show invalid input error or not
   * only if there is no error in maxlength, min and max,
   * this error will be shown to prevent multiple errors
   * display in the UI.
   *
   * @returns {boolean}
   */
  shouldShowInvalidFormatError = () =>
    !this.inputText.hasError('maxlength') &&
    !this.inputText.hasError('min') &&
    !this.inputText.hasError('max');

  /**
   * This function is called when the prefix icon is clicked.
   */
  onPrefixIconClick() {
    this.prefixIconClick.emit();
  }
}
